import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import styles from "./Toasts.module.css";
import { useSession } from "../auth/session";
import {
  type BellNotification,
  useNotifications,
} from "../data/api/notifications";
import { useRealtimeScope } from "../data/realtime/useRealtimeScope";
import type { NotificationKind } from "../data/types";
import { Icon } from "../icons";
import { cx } from "../lib/cx";
import { paths } from "../routing/paths";

const kindIcon = (kind: NotificationKind) => {
  switch (kind) {
    case "done":
      return <Icon.Check size={15} />;
    case "needs-input":
      return <Icon.Help size={15} />;
    case "review":
      return <Icon.PullRequest size={15} />;
    default:
      return <Icon.Bell size={15} />;
  }
};

const kindClass = (kind: NotificationKind): string =>
  cx(
    styles.ico,
    kind === "needs-input" && styles.needsInput,
    kind === "review" && styles.review,
    kind === "done" && styles.done,
  );

// Pops new notifications transiently as they arrive over realtime
// (`notification.posted` on the user's scope). The bell owns the full feed;
// this is the ambient layer on top.
export const Toasts = () => {
  const { user } = useSession();
  const { loading, markRead, notifications, refetch } = useNotifications();
  // New notifications fan out on the user's own scope; refetch when they land.
  useRealtimeScope(user ? `user:${user.public_id}` : null, refetch);
  const [toasts, setToasts] = useState<ReadonlyArray<BellNotification>>([]);
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    // Wait for the first fetch to resolve before bootstrapping, otherwise the
    // initial empty feed would make the whole loaded history look "new".
    if (loading) {
      return;
    }
    // First load: adopt the existing feed as "seen" so we don't toast history.
    if (seen.current === null) {
      seen.current = new Set(notifications.map((n) => n.id));
      return;
    }
    const known = seen.current;
    const fresh = notifications.filter((n) => !known.has(n.id) && !n.read);
    for (const n of notifications) {
      known.add(n.id);
    }
    if (fresh.length === 0) {
      return;
    }
    setToasts((current) => [...current, ...fresh].slice(-3));
    for (const n of fresh) {
      setTimeout(
        () => setToasts((current) => current.filter((x) => x.id !== n.id)),
        5200,
      );
    }
  }, [notifications, loading]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className={styles.host}>
      {toasts.map((n) => {
        const body = (
          <>
            <span className={kindClass(n.kind)}>{kindIcon(n.kind)}</span>
            <div style={{ minWidth: 0, textAlign: "left" }}>
              <div className={styles.text}>{n.text}</div>
              <div className={styles.meta}>
                {n.taskLabel ? `${n.taskLabel} · open →` : "open →"}
              </div>
            </div>
          </>
        );
        const toastClass = cx(styles.toast, "u-toast");
        const dismiss = (): void => {
          markRead(n.id);
          setToasts((current) => current.filter((x) => x.id !== n.id));
        };
        // The toast is a link to the task it announces — it says "open →", so
        // it opens in a new tab like anything else that does.
        return n.repoId && n.taskLabel ? (
          <Link
            className={toastClass}
            key={n.id}
            onClick={dismiss}
            to={paths.task(n.repoId, n.taskLabel)}
          >
            {body}
          </Link>
        ) : (
          <button
            className={toastClass}
            key={n.id}
            onClick={dismiss}
            type="button"
          >
            {body}
          </button>
        );
      })}
    </div>
  );
};
